import { firestore } from 'firebase/app';
import moment from 'moment';
import { DATE_INPUT_FORMAT_DEFAULT } from '../../../../appConstants';
import firebaseApp from '../../../../services/firebaseService';
import { EmployeeHelper, IUserSession } from '../../../employee';
import { IAuthState } from '../../../redux';
import { BasicBaseHelper, IBasicBaseClass } from '../../base';
import { ISPULine, ILPULine, TariffType } from '../tariffs/tariffs';
import TransformerHelper, { ITransformer } from '../transformer';
import lodash from 'lodash';
import TransformerJournalHelper from '../journal/journal';
import Decimal from 'decimal.js';
import uuid from 'uuid';
import { ILpuLineTotalSummary } from './line/lpu/lpuLineTotalSummary';

export interface ITransformerInvoice<T = ISPULine | ILPULine> extends IBasicBaseClass {
    guid : string;
    type : TariffType;

    /// Beginning of month for invoice
    monthDate : number;

    epNumber : string;

    /// Path to ep.
    epNumberRef : string;
    
    transformer : ITransformer;

    poleNumber : string;
    accountNumber : string;
    totalUsage : number;
    days : number;

    demandReading : number;
    reactiveEnergy : number;
    utilizedCapacity : number;

    credit : number; // Credit
    totalCharge : number; // Calculated Total - vat
    vatPercentage : number;
    vatCharge : number; // vat total amount in value, not the vat %
    totalDue : number; // Calculated Total + vat
    totalBilled : number; // Actual Total + vat
    totalInterest : number; // Total Interest Charged, no VAT

    paid : boolean;

    paymentDueDate : string | null;
    // Path to user
    paidBy : string | null;
    paidByName : string | null;
    paidByEmployeeNumber : string | null;
    paidOn : number | null;

    journaled : boolean;

    journal : string | null;
    journalNr : string | null;

    // Path to user
    journaledBy : string | null;
    journaledByName : string | null;
    journaledByEmployeeNumber : string | null;
    journaledOn : number | null;

    group : string;

    serviceCharge : number;
    standardConnectionCharge : number;

    lines : Array<T>;

    reverted : boolean;
    relatedId : string | null;
    related : ITransformerInvoice | null;

    isActive : boolean;
    
    //FOR SPU USE ONLY!!!
    adminCharge : number;
}

export class TransformerInvoiceHelper extends BasicBaseHelper {
    public static readonly COLLECTION_NAME = 'transformer_invoice';

    private static converter : <T = ILPULine | ISPULine>() => firebase.firestore.FirestoreDataConverter<ITransformerInvoice<T>> = <T = ILPULine | ISPULine>() => ({
        fromFirestore: (snapshot) => {
            return TransformerInvoiceHelper.fromFirestoreDocument<T>(snapshot);
        },
        toFirestore: (data : ITransformerInvoice<T>) : firebase.firestore.DocumentData => {
            return TransformerInvoiceHelper.toFirestoreDocument(data);
        },
    });
    
    public static fromFirestoreDocument<T = ILPULine | ISPULine>(snapshot : firebase.firestore.DocumentSnapshot) : ITransformerInvoice<T> {
        const result = super.fromFirestore(snapshot);
        const data = snapshot.data();

        if (!data || !result) throw new Error(`Document does not exist! ${snapshot.id}`);
        return {
            ...result,
            guid: data.guid ?? uuid.v4(),
            demandReading: data.demandReading ?? 0,
            adminCharge: data.adminCharge ?? 0,
            serviceCharge: data.serviceCharge ?? 0,
            standardConnectionCharge: data.standardConnectionCharge ?? 0,
            reactiveEnergy: data.reactiveEnergy ?? 0,
            utilizedCapacity: data.utilizedCapacity ?? 0,
            type: data.type,
            monthDate: data.monthDate.toMillis(),
            epNumber: data.epNumber,
            epNumberRef: data.epNumberRef.path,
            poleNumber: data.poleNumber,
            accountNumber: data.accountNumber,
            totalUsage: data.totalUsage,
            totalInterest: data.totalInterest ?? 0,
            days: data.days,
            totalCharge: data.totalCharge,
            vatPercentage: data.vatPercentage,
            vatCharge: data.vatCharge,
            totalDue: data.totalDue,
            totalBilled: data.totalBilled ?? 0,
            paid: !!data.paid,
            paidBy: data.paidBy?.path ?? null,
            paidByName: data.paidByName ?? null,
            paidByEmployeeNumber: data.paidByEmployeeNumber ?? null,
            paidOn: data.paidOn?.toMillis() ?? null,
            paymentDueDate: !data.paymentDueDate ? null : (moment.utc(data.paymentDueDate?.toMillis()).format('YYYY/MM/DD')),
            group: data.group,
            lines: data.lines,
            journal: !data.journal ? null : (data.journal as firestore.DocumentReference).id,
            journaled: !!data.journaled,
            journalNr: data.journalNr ?? null,
            journaledBy: data.journaledBy?.path ?? null,
            journaledByName: data.journaledByName ?? null,
            journaledByEmployeeNumber: data.journaledByEmployeeNumber ?? null,
            journaledOn: data.journaledOn?.toMillis() ?? null,
            relatedId: !data.relatedId ? null : (data.relatedId as firestore.DocumentReference).path,
            related: data.related ?? null,
            reverted: !!data.reverted,
            transformer: data.transformer,
            credit: data.credit ?? 0,
            isActive: data.isActive ?? true,
        };
    }

    public static toFirestoreDocument<T = ILPULine | ISPULine>(data : ITransformerInvoice<T>) {
        const result = super.toFirestore(data);
        return {
            ...result,
            type: data.type,
            monthDate: firestore.Timestamp.fromMillis(data.monthDate),
            epNumber: data.epNumber,
            epNumberRef: TransformerHelper.path(data.epNumberRef),
            poleNumber: data.poleNumber,
            accountNumber: data.accountNumber,
            totalUsage: data.totalUsage,
            days: data.days,
            totalInterest: data.totalInterest,
            totalCharge: data.totalCharge,
            vatPercentage: data.vatPercentage,
            vatCharge: data.vatCharge,
            totalDue: data.totalDue,
            paid: !!data.paid,
            paidBy: !data.paidBy ? null : EmployeeHelper.path(data.paidBy),
            paidByName: data.paidByName ?? null,
            paidByEmployeeNumber: data.paidByEmployeeNumber ?? null,
            paidOn: !data.paidOn ? null : firestore.Timestamp.fromMillis(data.paidOn),
            paymentDueDate: !data.paymentDueDate ? null : firestore.Timestamp.fromMillis(moment.utc(data.paymentDueDate, DATE_INPUT_FORMAT_DEFAULT, true).valueOf()),
            group: data.group,
            lines: data.lines,
            journal: !data.journal ? null : TransformerJournalHelper.doc(data.journal),
            journaled: !!data.journaled,
            journalNr: data.journalNr ?? null,
            journaledBy: !data.journaledBy ? null : EmployeeHelper.path(data.journaledBy),
            journaledByName: data.journaledByName ?? null,
            journaledByEmployeeNumber: data.journaledByEmployeeNumber ?? null,
            journaledOn: !data.journaledOn ? null : firestore.Timestamp.fromMillis(data.journaledOn),
            related: !data.related ? null : data.related,
            relatedId: !data.related ? null : TransformerInvoiceHelper.doc(data.related.id),
            reverted: !!data.reverted,
            transformer: data.transformer,
            totalBilled: data.totalBilled,
            demandReading: data.demandReading,
            adminCharge: data.adminCharge,
            serviceCharge: data.serviceCharge,
            reactiveEnergy: data.reactiveEnergy,
            utilizedCapacity: data.utilizedCapacity,
            isActive: data.isActive,
            guid: data.guid,
        };
    }

    public static collection<T = ILPULine | ISPULine>() {
        return firebaseApp.firestore().collection(TransformerInvoiceHelper.COLLECTION_NAME).withConverter(this.converter<T>());
    }

    public static doc(id ?: string) {
        if (!id) {
            return this.collection().doc();
        }
        return this.collection().doc(id);
    }

    public static saveMany(invoices : Array<ITransformerInvoice>) {
        return firebaseApp.firestore().runTransaction(async (db) => {
            invoices.forEach(invoice => {
                db.set(TransformerInvoiceHelper.doc(invoice.id), invoice);
            });
        });
    }

    private static reverse(invoice : ITransformerInvoice, auth : IAuthState) {
        return {
            ...invoice,
            totalCharge: invoice.totalCharge * -1,
            vatCharge: invoice.vatCharge * -1,
            totalDue: invoice.totalDue * -1,
            paid: false,
            paidBy: null,
            paidByName: null,
            paidByEmployeeNumber: null,
            paidOn: null,
            paymentDueDate: null,
            journaled: false,
            journalNr: null,
            journaledBy: null,
            journaledByName: null,
            journaledByEmployeeNumber: null,
            journaledOn: null,
            related: invoice,
            relatedId: invoice.id,
            createdBy: auth.session?.firebaseUser.uid ?? '',
            createdByEmployee: auth.session?.employee.EmployeeNumber ?? '',
            createdByName: auth.session?.employee.Name ?? '',
            createdOn: moment.utc().valueOf(),
            updatedBy: auth.session?.firebaseUser.uid ?? '',
            updatedByEmployee: auth.session?.employee.EmployeeNumber ?? '',
            updatedByName: auth.session?.employee.Name ?? '',
            updatedOn: moment.utc().valueOf(),
            reverted: false,
        };
    }

    public static reverseMany(db : firestore.Transaction, invoices : Array<ITransformerInvoice>, auth : IAuthState) {
        invoices.forEach(invoice => db.set(TransformerInvoiceHelper.doc(), TransformerInvoiceHelper.reverse(invoice, auth)));
        invoices.forEach(invoice => db.update(TransformerInvoiceHelper.doc(invoice.id), {
            updatedBy: EmployeeHelper.doc(auth.session?.firebaseUser.uid ?? ''),
            updatedByEmployee: auth.session?.employee.EmployeeNumber ?? '',
            updatedByName: auth.session?.employee.Name ?? '',
            updatedOn: firestore.Timestamp.fromMillis(moment.utc().valueOf()),
            reverted: true,
        }));
    }

    public static calculateLPUTotal(invoice : ITransformerInvoice<ILPULine>) : ITransformerInvoice<ILPULine> {
        const result = { 
            ...invoice,
            lines: [
                ...invoice.lines,
            ],
        };

        const isTariffChange = invoice.lines.length > 1;

        result.days = result.lines.reduce((a, b) => a + b.days, 0);

        let totalInterest = 0;
        result.lines.forEach((line) => {
            const totalLineSummary = this.calculateLPULineTotalSummary(invoice, line, isTariffChange);
            const lineTotal = this.calculateLPULineTotal(totalLineSummary);

            line.ancillaryServiceCharge = totalLineSummary.totalAncillaryServiceCharge;
            line.generationCapacityCharge = totalLineSummary.totalGenerationCapacityCharge;
            
            line.total = lineTotal;
            totalInterest += line.interestCharge;
        });

        let totalCharge = result.serviceCharge + result.standardConnectionCharge;

        result.lines.forEach((line) => {
            totalCharge += (line.total);
        });

        const totalVat = totalCharge * result.vatPercentage;

        result.totalCharge = parseFloat(totalCharge.toFixed(2));
        result.vatCharge = parseFloat(totalVat.toFixed(2));
        result.totalDue = parseFloat((totalCharge + totalVat + totalInterest).toFixed(2));
        result.totalBilled = parseFloat((totalCharge + totalVat).toFixed(2));
        result.totalInterest = totalInterest;
        result.totalUsage = lodash.sumBy(result.lines, x => x.networkDemandUnits);

        return result;
    }

    private static calculateLPULineTotalSummary(invoice : ITransformerInvoice<ILPULine>, line : ILPULine, isTariffChange : boolean) : ILpuLineTotalSummary {
        // Additional Charges        
        const totalNetworkDemandCharge = (line.networkDemandUnits * line.tariffValues.networkDemandCharge / 100);

        const totalAncillaryServiceCharge = parseFloat((line.networkDemandUnits * line.tariffValues.ancillaryServiceCharge / 100).toFixed(2));
        
        const totalLegacyCharge = ((line.legacyCharge ?? 0) * ((line.tariffValues.legacyCharge ?? 0) / 100));
        
        const totalAdminCharge = ((line.adminCharge ?? 0) * line.days);
        
        const totalExcessNetworkAccessCharge = (line.excessNetworkAccessCharge * line.tariffValues.netwrokCapacity);
        
        const totalExcessReactiveEnergyCharge = (line.excessReactiveEnergy * line.tariffValues.reactiveEnergyCharge / 100);

        let totalGenerationCapacityCharge = 0;
        if (isTariffChange) {
            totalGenerationCapacityCharge = parseFloat((invoice.utilizedCapacity * (line.tariffValues.generationCapacityCharge ?? 0) / 31 * line.days).toFixed(2))
        } else {
            totalGenerationCapacityCharge = parseFloat((invoice.utilizedCapacity * (line.tariffValues.generationCapacityCharge ?? 0)).toFixed(2))
        }

        let totalNetworkCapacityCharge = 0;
        if (isTariffChange) {
            totalNetworkCapacityCharge = (invoice.utilizedCapacity * line.tariffValues.netwrokCapacity / 31 * line.days);
        } else {
            totalNetworkCapacityCharge = (invoice.utilizedCapacity * line.tariffValues.netwrokCapacity);
        }

        // Low Season Charges
        const totalUnitsSTDLowCharge = (line.unitsSTDLow * line.tariffValues.lowSTD / 100);
        
        const totalUnitsOffPeakLowCharge = (line.unitsOffPeakLow * line.tariffValues.lowOffPeak / 100);
        
        const totalUnitsPeakLowCharge = (line.unitsPeakLow * line.tariffValues.lowPeak / 100);

        // High Season Charges
        const totalUnitsSTDHighCharge = (line.unitsSTDHigh * line.tariffValues.highSTD / 100);
        
        const totalUnitsOffPeakHighCharge = (line.unitsOffPeakHigh * line.tariffValues.highOffPeak / 100);
        
        const totalUnitsPeakHighCharge = (line.unitsPeakHigh * line.tariffValues.highPeak / 100);

        const totalSummary : ILpuLineTotalSummary = {
            totalNetworkCapacityCharge,
            totalNetworkDemandCharge,
            totalAncillaryServiceCharge,
            totalGenerationCapacityCharge,
            totalLegacyCharge,
            totalExcessNetworkAccessCharge,
            totalExcessReactiveEnergyCharge,
            totalUnitsSTDLowCharge,
            totalUnitsOffPeakLowCharge,
            totalUnitsPeakLowCharge,
            totalUnitsSTDHighCharge,
            totalUnitsOffPeakHighCharge,
            totalUnitsPeakHighCharge,
            totalAdminCharge,
        };

        return totalSummary;
    }
    
    private static calculateLPULineTotal(lineTotalSummary : ILpuLineTotalSummary) : number {
        return lineTotalSummary.totalNetworkCapacityCharge
        + lineTotalSummary.totalNetworkDemandCharge
            + lineTotalSummary.totalAncillaryServiceCharge
            + lineTotalSummary.totalGenerationCapacityCharge
            + lineTotalSummary.totalLegacyCharge
            + lineTotalSummary.totalExcessNetworkAccessCharge
            + lineTotalSummary.totalExcessReactiveEnergyCharge

            + lineTotalSummary.totalUnitsSTDLowCharge
            + lineTotalSummary.totalUnitsOffPeakLowCharge
            + lineTotalSummary.totalUnitsPeakLowCharge
            
            + lineTotalSummary.totalUnitsSTDHighCharge
            + lineTotalSummary.totalUnitsOffPeakHighCharge
            + lineTotalSummary.totalUnitsPeakHighCharge
            
            + lineTotalSummary.totalAdminCharge;
    };

    public static calculateSPUTotal(invoice : ITransformerInvoice<ISPULine>) {
        const result = { 
            ...invoice,
            lines: [
                ...invoice.lines,
            ],
        };

        result.days = result.lines.reduce((a, b) => a + b.days, 0);

        result.lines.forEach((line) => {
            line.total = TransformerInvoiceHelper.calculateSPULineTotal(line);
        });
        
        const adminCharge = new Decimal(result.adminCharge).mul(result.days);
        let total = adminCharge.add(result.serviceCharge).add(result.credit);

        result.lines.forEach((line) => {
            total = total.add(line.total);
        });

        const totalVat = total.mul(result.vatPercentage);

        const totalDue = totalVat.add(total);

        result.totalCharge = total.toDecimalPlaces(2).toNumber();
        result.vatCharge = totalVat.toDecimalPlaces(2).toNumber();
        result.totalDue = totalDue.toDecimalPlaces(2).toNumber();
        result.totalBilled = totalDue.toDecimalPlaces(2).toNumber();
        result.totalUsage = lodash.sumBy(result.lines, x => x.totalUsage);

        return result;
    }

    public static calculateSPULineTotal(line : ISPULine) {
        const serviceCharge = new Decimal(line.tariffValues.serviceCharge).mul(line.days);
        const networkCapacity = new Decimal(line.tariffValues.networkCapacity).mul(line.days);
        const networkDemand = new Decimal(line.tariffValues.networkDemand).div(100).mul(line.totalUsage);
        const ancillaryService = new Decimal(line.tariffValues.ancillaryService).div(100).mul(line.totalUsage);
        const energyCharge = new Decimal(line.tariffValues.energyCharge).div(100).mul(line.totalUsage);
        const generationCapacityCharge = new Decimal(line.tariffValues.generationCapacityCharge ?? 0).mul(line.days);

        const totalCharges = serviceCharge
            .add(networkCapacity)
            .add(networkDemand)
            .add(ancillaryService)
            .add(energyCharge)
            .add(generationCapacityCharge)
            .toNumber();

        return totalCharges;
    }

    public static createInactiveSave = <T = ILPULine | ISPULine>(session : IUserSession, entry : ITransformerInvoice<T>) : ITransformerInvoice<T> => {        
        return {
            ...entry,
            updatedByEmployee: session.employee.EmployeeNumber ?? '',
            updatedByName: session.employee.Name,
            updatedBy: session.firebaseUser.uid,
            updatedOn: moment.utc().valueOf(),
            isActive: false,
        };
    };

    public static async save(entry : ITransformerInvoice) {
        if (entry.id) {
            this
                .collection()
                .doc(entry.id)
                .set(entry, { merge: true });
        } else {
            this
                .collection()
                .add(entry);
        }
    }
}
